import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { ToastComponent } from '../../shared/toast/toast.component';
import { PoliticianService } from '../../services/politician.service';
import { MiscService } from '../../services/misc.service';
import { Government } from '../../shared/models/government.model';
import { Legislature } from '../../shared/models/legislature.model';
import { Commission } from '../../shared/models/commission.model';
import { Party } from '../../shared/models/party.model';
import { Media } from '../../shared/models/media.model';
import { setClassInvalid, toSentenceCase } from '../../shared/functions';
import {
  clusters, collegeRegions, dateRegexp, ee, offices, officesNoPortfolio, officesOther,
  organs, roles, rolesMedia, rolesOther, usernameRegexp
} from '../../shared/constants';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-add-stakeholder-form',
  templateUrl: './add-stakeholder-form.component.html',
  standalone: false
})
export class AddStakeholderFormComponent implements OnInit, OnDestroy {
  private politicianService = inject(PoliticianService);
  private miscService = inject(MiscService);
  private formBuilder = inject(UntypedFormBuilder);
  toast = inject(ToastComponent);


  @Input() governments: Government[] = [];
  @Input() legislatures: Legislature[] = [];
  @Input() commissionsCamera: Commission[] = [];
  @Input() commissionsSenato: Commission[] = [];
  @Input() commissionsEU: Commission[] = [];
  @Input() parties: Party[] = [];
  @Input() partiesEU: Party[] = [];
  @Input() medias: Media[] = [];

  today = new Date().toISOString().split('T')[0];
  addStakeholderForm = this.createStakeholderForm();
  politicianTopics = [];
  clusters = clusters;
  organs = organs;
  collegeRegions = collegeRegions;
  offices = offices;
  officesNoPortfolio = officesNoPortfolio;
  officesOther = officesOther;
  roles = roles;
  rolesMedia = rolesMedia;
  rolesOther = rolesOther;
  usernameRegexp = usernameRegexp;
  dateRegexp = dateRegexp;
  setClassInvalid = setClassInvalid;

  ngOnInit(): void {
    ee.on('searchInput', (obj: any) => this.addStakeholderForm.patchValue(obj));
  }

  ngOnDestroy(): void {
    ee.off('searchInput', () => {});
  }

  changeContent(content: string): void {
    ee.emit('changeContent', content);
  }

  createStakeholderForm(): UntypedFormGroup {
    return this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      category: ['', Validators.required],
      email: ['', Validators.email],
      phone: [''],
      birthday: ['', Validators.pattern(/\d{4}-\d{2}-\d{2}/)],
      birthplace: [''],
      gender: ['', Validators.required],
      education: [''],
      college: [''],
      _party: [''],
      _commissions: [''],
      job: ['', Validators.required],
      organ: [''],
      office: [''],
      _governments: [[]],
      _legislatures: [[]],
      _media: [''],
      notes: [''],
      topics: [[]],
      english: [''],
      socials: this.formBuilder.group({
        twitter: this.formBuilder.group({
          username: ['', Validators.pattern(usernameRegexp.twitter)]
        }),
        instagram: this.formBuilder.group({
          username: ['', Validators.pattern(usernameRegexp.instagram)]
        }),
        facebook: this.formBuilder.group({
          username: ['', Validators.pattern(usernameRegexp.facebook)]
        }),
        linkedin: this.formBuilder.group({
          username: ['', Validators.pattern(usernameRegexp.linkedin)]
        })
      })
    });
  }

  selectCategory(category: string): void {
    this.addStakeholderForm.patchValue({ category });
  }

  setGender(): void {
    const firstName = this.addStakeholderForm.get('firstName').value;
    this.miscService.gender(firstName)
      .subscribe({
        next: res => this.addStakeholderForm.patchValue({ gender: res.gender }),
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  fieldToSentenceCase(fieldName: string): void {
    const field = this.addStakeholderForm.get(fieldName).value;
    this.addStakeholderForm.patchValue({ [fieldName]: toSentenceCase(field) });
  }

  selectCluster(cluster: string): void {
    if (this.politicianTopics.includes(cluster)) {
      const idx = this.politicianTopics.indexOf(cluster);
      this.politicianTopics.splice(idx, 1);
    } else {
      this.politicianTopics.push(cluster);
    }
    this.addStakeholderForm.patchValue({ topics: this.politicianTopics });
  }

  formatUsername(social: string): void {
    const username = this.addStakeholderForm.get(`socials.${social}.username`).value;
    this.addStakeholderForm.patchValue({
      socials: {
        [social]: {
          username: (username || '').trim().replace(/\s+|@/g, '')
        }
      }
    });
  }

  addStakeholder(): void {
    this.politicianService.add(this.addStakeholderForm.value)
      .pipe(finalize(() => {
        ee.emit('getStakeholders');
        ee.emit('stakeholderAdded', true);
      }))
      .subscribe({
        next: res => this.toast.setMessage('Stakeholder added successfully!', 'success'),
        error: err => this.toast.setMessage(err.error.message, 'danger')
      });
  }
}
